import { Injectable } from '@angular/core';
import { Agency } from '@app/models';
import { CartechCapability, ElectroPneumaticBrake, EmergencyBrakeOverride } from '@app/pcs/dossier/models';
import { DictionaryEntity, ProcessType, TimetablePeriod, TypeOfLoco } from '@app/pcs/models';
import { ActivityType } from '@app/pcs/models/activity-type';
import { filterTimetablePeriodsForCarryForward } from '@app/pcs/timetable-periods/utils/timetable-periods-util';
import { hasValue } from '@util';
import * as R from 'ramda';

export const NONE_LOCATION_TYPE_DESCRIPTION = 'locationType.none';
export const noneLocationType = R.pathEq(['description'], NONE_LOCATION_TYPE_DESCRIPTION);
export const notNoneLocationType = R.compose(R.not, noneLocationType);

@Injectable()
export class DictionaryService {
  private dictionaries: any;

  setDictionaries(dictionaries: any): void {
    this.dictionaries = dictionaries;
  }

  getDictionaries(): any {
    return this.dictionaries;
  }

  getProcessTypes = (): any => R.prop('processTypes', this.dictionaries);

  getCatalogueProcessTypes = (): any => R.prop('catalogueProcessTypes', this.dictionaries);

  getProcessTypesForRuImPairDossiers = (): any => R.filter((processType: ProcessType) =>
    R.none(R.propEq('id', processType.id), this.getCatalogueProcessTypes()), this.getProcessTypes());

  getProcessTypesSupportingPermanentAcceptance = (): any => R.prop('processTypesSupportingPermanentAcceptance', this.dictionaries);

  getProcessTypesSupportingResponsibleRu = (): any => R.prop('processTypesSupportingResponsibleRu', this.dictionaries);

  getTimetablePeriods = (): any => {
    const timetablePeriods = R.prop('timetablePeriods', this.dictionaries);
    return hasValue(timetablePeriods) ? timetablePeriods : [];
  };

  setTimetablePeriods = (timetablePeriods: TimetablePeriod[]) => this.setDictionaries(R.mergeDeepRight(this.dictionaries, {timetablePeriods}));

  getTimetablePeriodsForCarryForward = (): TimetablePeriod[] => filterTimetablePeriodsForCarryForward(this.getTimetablePeriods());

  getTimetablePeriodsActiveForDossierCreation = (): any => R.filter((ttp: any) => ttp.activeForDossierCreation, this.getTimetablePeriods());

  getDossierTypes = (): any => R.prop('dossierTypesForNewDossier', this.dictionaries);

  getDossierStatuses = (): any => R.prop('dossierStatuses', this.dictionaries);

  getDossierStatusesSupportingPermanentAcceptance = (): any => R.prop('dossierStatusesSupportingPermanentAcceptance', this.dictionaries);

  getDossierStatusesSupportingResponsibleRu = (): any => R.prop('dossierStatusesSupportingResponsibleRu', this.dictionaries);

  getObservationTypes = (): any => R.prop('observationTypes', this.dictionaries);

  getAgencies = (): any => R.prop('allActiveAgencies', this.dictionaries);

  setAgencies = (agencies: Agency[]) => this.setDictionaries(R.mergeDeepRight(this.dictionaries, {allActiveAgencies: agencies}));

  getTrainTypes = (): any => R.prop('trainTypes', this.dictionaries);

  getDefaultTimetablePeriod = (): any => R.prop('defaultTimetablePeriod', this.dictionaries);

  getDefaultTimetablePeriodForCarryForward = (): any => R.compose(R.head, this.getTimetablePeriodsForCarryForward)();

  getProgressStatuses = (): any => R.prop('progressStatuses', this.dictionaries);

  getLocationTypes = (): DictionaryEntity[] => R.prop('locationTypes', this.dictionaries);

  getAgenciesWithoutNoneLocationType = (): number[] => R.prop('agenciesWithoutNoneLocationType', this.dictionaries);

  getLocationTypesForImAgency = (imAgencyId: number): DictionaryEntity[] => R.includes(imAgencyId, this.getAgenciesWithoutNoneLocationType())
    ? R.filter(notNoneLocationType, this.getLocationTypes())
    : this.getLocationTypes();

  getActivityTypes = (): ActivityType[] => R.prop('activityTypes', this.dictionaries);

  getActivityTypesForTtpAndImAgency = (ttpId: number, imAgencyId: number): ActivityType[] => R.filter(activityType =>
    activityType.predefined || (activityType.ttpId === ttpId && activityType.agency.id === imAgencyId), this.getActivityTypes()
  );

  getDefaultLocationType = (imAgency: number): DictionaryEntity => R.find(noneLocationType, this.getLocationTypesForImAgency(imAgency));

  getTiltingFunctions = (): any => R.prop('tiltingFunctions', this.dictionaries);

  getRouteClasses = (): any => R.prop('routeClassValues', this.dictionaries);

  getUnitPositions = (): any => R.prop('unitPositions', this.dictionaries);

  getTractionRoles = (): any => R.prop('tractionRoles', this.dictionaries);

  getTrainRadioSystems = (): any => R.prop('trainRadioSystems', this.dictionaries);

  getBrakeTypes = (): any => R.prop('brakeTypes', this.dictionaries);

  getUserMinPasswordLength = (): any => R.prop('userMinPasswordLength', this.dictionaries);

  getCorridors = (): any => R.prop('corridors', this.dictionaries);

  getRfcDossierTypes = (): any => R.prop('rfcDossierTypes', this.dictionaries);

  getParameterSetCodeFields = (): any => R.prop('parameterSetCodeFields', this.dictionaries);

  getMaxAxleWeightMinValue = (): any => R.prop('maxAxleWeightMinValue', this.dictionaries);

  getMaxAxleWeightMaxValue = (): any => R.prop('maxAxleWeightMaxValue', this.dictionaries);

  getCargoBrakeTypes = (): DictionaryEntity[] => R.prop('cargoBrakeTypes', this.dictionaries);

  getBanWaggons = (): DictionaryEntity[] => R.prop('banWaggons', this.dictionaries);

  getEmergencyBrakeOverrides = (): EmergencyBrakeOverride[] => R.prop('emergencyBrakeOverrides', this.dictionaries);

  getElectroPneumaticBrakes = (): ElectroPneumaticBrake[] => R.prop('electroPneumaticBrakes', this.dictionaries);

  getCartechCapabilities = (): CartechCapability[] => R.prop('cartechCapabilities', this.dictionaries);

  getDirectionOfAlignments = (): DictionaryEntity[] => R.prop('directionOfAlignments', this.dictionaries);

  getConnectionKindTypes = (): DictionaryEntity[] => R.prop('connectionKindTypes', this.dictionaries);

  getCompositeRelationTypes = (): DictionaryEntity[] => R.prop('compositeRelationTypes', this.dictionaries);

  getTimetableShiftingTypes = (): DictionaryEntity[] => R.prop('timetableShiftingTypes', this.dictionaries);

  getTimeZoneOffsets = (): any => R.prop('timeZoneOffsets', this.dictionaries);

  getTrainCcSystems = (): DictionaryEntity[] => R.prop('trainCcSystems', this.dictionaries);

  getTypeOfEngines = (): DictionaryEntity[] => R.prop('typeOfEngines', this.dictionaries);

  getTypeOfLocos = (): DictionaryEntity<TypeOfLoco>[] => R.prop('typeOfLocos', this.dictionaries);

  getPhaseGroups = (): any => R.prop('phaseGroups', this.dictionaries);

  getPredefinedPageSizes = (): any => R.prop('predefinedPageSizes', this.dictionaries);

  getCatalogueTypes = (): DictionaryEntity[] => R.prop('catalogueTypes', this.dictionaries);

  getDirections = () => R.prop('directions', this.dictionaries);

  getPapRequestStatuses = (): any => R.prop('papRequestStatuses', this.dictionaries);

  getSupportedCatalogueTypes = (): any => {
    const papProcessType = R.prop('id', R.find(R.propEq('id', 'P'), this.getProcessTypes()));
    return R.filter(R.propEq('id', papProcessType), this.getCatalogueTypes());
  };

  getAgencyTypeIds = (): any => R.prop('agencyTypeIds', this.dictionaries);

  getAgencySubTypeIds = (): any => R.prop('agencySubTypeIds', this.dictionaries);

  getUserTypes = (): any => R.prop('allUserTypes', this.dictionaries);

  getUserRoles = (): any => R.prop('allUserRoles', this.dictionaries);

  getWriteOperations = (): any => R.prop('writeOperations', this.dictionaries);

  getMediationProtocolStatuses = (): any => R.prop('mediationProtocolStatuses', this.dictionaries);

  getDossierTypesForSearch = (): any => R.prop('dossierTypesForSearch', this.dictionaries);

  getRfcKpiMilestoneDataTypes = (): any[] => R.prop('rfcKpiMilestoneDataTypes', this.dictionaries);

  getRfcKpiTypes = (): DictionaryEntity[] => R.prop('rfcKpiTypes', this.dictionaries);

  getHybridLocoTypes = (): any => R.prop('hybridLocoTypes', this.dictionaries);

  getLengthOfLocoTypeSerialNumber = (): any => R.prop('lengthOfLocoTypeSerialNumber', this.dictionaries);

  getLengthOfLocoTypeSeriesNumber = (): any => R.prop('lengthOfLocoTypeSeriesNumber', this.dictionaries);

  getCommentTypes = (): any => R.prop('commentTypes', this.dictionaries);

  getParameterLimitations = (): any => R.prop('parameterLimitations', this.dictionaries);

  getPackageGroups = (): any => R.prop('packageGroups', this.dictionaries);

  getDangerLabels = (): any => R.prop('dangerLabels', this.dictionaries);
  getDangerLabelsSelectionLimit = (): any => R.prop('dangerLabelsSelectionLimit', this.dictionaries);

  getCommercialTrafficTypes = (): DictionaryEntity[] => R.prop('commercialTrafficTypes', this.dictionaries);

  getSpecialServices = (): DictionaryEntity[] => R.prop('specialServices', this.dictionaries);

  getFacilityTypes = (): DictionaryEntity[] => R.prop('facilityTypes', this.dictionaries);

  getCharacteristics = (): DictionaryEntity[] => R.prop('characteristics', this.dictionaries);

  getPapIntentionTypes = (): any => R.prop('papIntentionTypes', this.dictionaries);

  getReadOnlyStartDate = (): any => R.prop('readOnlyStartDate', this.dictionaries);

  showInfoForReadOnlyStartDate = (): any => R.prop('showInfoForReadOnlyStartDate', this.dictionaries);

  isReadOnlyModeActive = (): any => !this.showInfoForReadOnlyStartDate() && hasValue(this.getReadOnlyStartDate());

  resetState = () => this.dictionaries = null;

}
